import { TAdminRecord } from '@/shared/types/admin-api';

import { VIDEO_TYPES } from '@/shared/constants';

import {
  formatActorComplaintReasons,
  formatActorComplaintReasonsSummary
} from './adminActorComplaint';
import { getAnyComplaintReasonLabel } from './adminParticipantComplaint';

const ENCODING_STATUS_LABELS: Record<string, string> = {
  encoding_pending: 'В очереди',
  encoding_in_progress: 'Обрабатывается',
  encoding_done: 'Готово',
  encoding_failed: 'Ошибка'
};

export const GLOBAL_VALUE_LABELS: Record<string, string> = {
  active: 'Активен',
  actor: 'Соискатель',
  'animated-series': 'Мультсериал',
  anime: 'Аниме',
  approved: 'Одобрено',
  archive: 'В архиве',
  archived: 'В архиве',
  blocked: 'Заблокирован',
  cartoon: 'Мультфильм',
  casting_agency: 'Кастинг-агентство',
  cinema_company: 'Кинокомпания',
  collected: 'Собран',
  completed: 'Завершён',
  complete: 'Завершён',
  content_manager: 'Контент-менеджер',
  confirmed: 'Подтверждено',
  converted: 'В проектах',
  dance_style: 'Танцы',
  declined: 'Отклонён',
  deleted: 'Удалён',
  director: 'Работодатель',
  did_not_participate: 'Не участвовал',
  draft: 'Черновик',
  extra: 'Дополнительно',
  failed: 'Ошибка',
  female: 'Женский',
  fixed: 'Фиксированная',
  in_progress: 'В работе',
  male: 'Мужской',
  moderator: 'Модератор',
  movie: 'Фильм',
  musical_instrument: 'Музыкальные инструменты',
  new: 'Новый',
  not_registered: 'Не зарегистрирован',
  offered: 'Предложен',
  pending: 'Ожидает',
  on_moderation: 'На модерации',
  personal_data_protection: 'Защита персональных данных',
  preproduction: 'Препродакшен',
  registered: 'Зарегистрирован',
  rejected: 'Отклонён',
  resolved: 'Решён',
  running: 'Выполняется',
  skipped: 'Пропущен',
  sport: 'Спорт',
  site_rules: 'Правила сайта',
  superadmin: 'Главный администратор',
  support: 'Поддержка',
  success: 'Успешно',
  'tv-series': 'Сериал',
  tv_show: 'Телешоу',
  video_blog: 'Видеоблог',
  waiting: 'Ожидает',
  vocal_style: 'Вокал'
};

const FIELD_VALUE_LABELS: Record<string, Record<string, string>> = {
  author_type: {
    AdminUser: 'Администратор',
    User: 'Пользователь'
  },
  category: GLOBAL_VALUE_LABELS,
  encoding_status: ENCODING_STATUS_LABELS,
  manufacturer: {
    iOS: 'Apple',
    ios: 'Apple'
  },
  platform: {
    android: 'Android',
    ios: 'iOS'
  },
  project_type: {
    movie: 'Кино',
    series: 'Сериал',
    short_movie: 'Короткий метр',
    short: 'Короткий метр'
  },
  role: GLOBAL_VALUE_LABELS,
  video_type: VIDEO_TYPES,
  moderation_status: {
    approved: 'Одобрено',
    blocked: 'Отклонено',
    pending: 'На модерации',
    rejected: 'Отклонено',
    waiting: 'Ожидает'
  },
  salary_period: {
    day: 'День',
    fixed: 'Фиксированная',
    hour: 'Час',
    month: 'Месяц',
    project: 'Проект',
    shift: 'Смена'
  },
  resource_type: {
    ActorCard: 'Визитка',
    Casting: 'Кастинг',
    Company: 'Компания',
    Document: 'Документ',
    Excerpt: 'Видео читка',
    Film: 'Фильм',
    Invitation: 'Приглашение',
    LegalEntity: 'Юридическое лицо',
    Project: 'Проект',
    ProjectCategory: 'Категория проекта',
    ProjectParticipant: 'Участник проекта',
    Response: 'Отклик',
    User: 'Пользователь'
  },
  // Журнал импортов МКРФ общий на два реестра (19744)
  dataset: {
    national_film_identities: 'Национальные фильмы (УНФ)',
    register_movies: 'Прокатные удостоверения'
  },
  status: GLOBAL_VALUE_LABELS,
  state: GLOBAL_VALUE_LABELS,
  // Скоупно, не в GLOBAL_VALUE_LABELS: ключ web глобально зацепил бы чужие поля
  // (например, свободный текст `source` у изображений новостей)
  source: {
    mobile: 'Приложение',
    web: 'Веб'
  },
  user_activity: GLOBAL_VALUE_LABELS
};

/**
 * Проверяет record-like объект.
 * @param value Значение
 */
export const isAdminRecord = (value: unknown): value is TAdminRecord => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Возвращает URL вложения.
 * @param value Значение вложения
 */
export const getAdminAttachmentUrl = (value: unknown) => {
  if (typeof value === 'string' && value) return value;
  if (isAdminRecord(value) && typeof value.url === 'string') return value.url;

  return null;
};

/**
 * Возвращает человекочитаемое имя record-like объекта.
 * @param value Значение
 */
export const getAdminRecordLabel = (value: unknown) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (!isAdminRecord(value)) return null;

  // Берём первое НЕпустое значение: у юзера без имени full_name === '', и пустая
  // строка перебивала бы id — колонка рендерила «Подробнее» вместо номера
  const label = [
    value.label,
    value.name,
    value.title,
    value.full_name,
    value.email,
    value.phone,
    value.value,
    value.id
  ].find(
    item =>
      (typeof item === 'string' && item.trim() !== '') ||
      typeof item === 'number'
  );

  return label === undefined ? null : String(label);
};

/**
 * Разбирает ISO-строку даты; пустое, не строка или невалидное — null
 * @param value ISO date / datetime
 */
const parseAdminDate = (value?: unknown) => {
  if (typeof value !== 'string' || !value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Форматирует дату.
 * @param value ISO date
 */
export const formatAdminDate = (value?: unknown) => {
  const date = parseAdminDate(value);
  if (!date) return '—';

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

/**
 * Форматирует дату и время.
 * @param value ISO datetime
 */
export const formatAdminDateTime = (value?: unknown) => {
  const date = parseAdminDate(value);
  if (!date) return '—';

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

/**
 * Проверяет date/date-time поле.
 * @param field API key
 */
export const isAdminDateField = (field: string) => {
  return field.endsWith('_at') || field.endsWith('_date');
};

/**
 * Проверяет поле статуса.
 * @param field API key
 */
export const isAdminStatusField = (field: string) => {
  return [
    'activity',
    'category',
    'channel',
    'encoding_status',
    'kind',
    'moderation_status',
    'profile_visibility',
    'project_status',
    'project_type',
    'role',
    'salary_period',
    'source',
    'state',
    'status',
    'type',
    'user_activity',
    'visibility'
  ].includes(field);
};

/**
 * Форматирует диапазон `{from,to}`.
 * @param value Значение
 */
const formatAdminRange = (value: TAdminRecord) => {
  const from = formatAdminDate(value.from);
  const to = formatAdminDate(value.to);

  if (from !== '—' && to !== '—') return `с ${from} по ${to}`;
  if (from !== '—') return `с ${from}`;
  if (to !== '—') return `по ${to}`;

  return '—';
};

/**
 * Возвращает label enum-значения.
 * @param field API key
 * @param value Значение
 */
const getStaticValueLabel = (field: string, value: unknown) => {
  if (typeof value !== 'string') return null;

  return (
    FIELD_VALUE_LABELS[field]?.[value] || GLOBAL_VALUE_LABELS[value] || null
  );
};

/**
 * Форматирует любое admin-значение без контекста записи.
 * @param value Значение
 */
const formatAdminString = (value: string) => {
  if (GLOBAL_VALUE_LABELS[value]) return GLOBAL_VALUE_LABELS[value];

  const text = value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '—';

  return text.length > 90 ? `${text.slice(0, 90)}...` : text;
};

/**
 * Форматирует массив admin-значений.
 * @param value Значение
 */
const formatAdminArray = (value: unknown[]) => {
  const labels = value
    .map(getAdminRecordLabel)
    .filter((label): label is string => Boolean(label));

  return labels.length ? labels.join(', ') : '—';
};

/**
 * Форматирует record-like admin-значение.
 * @param value Значение
 */
const formatAdminRecordValue = (value: TAdminRecord) => {
  if ('from' in value || 'to' in value) return formatAdminRange(value);

  const attachmentUrl = getAdminAttachmentUrl(value);
  if (attachmentUrl) return getAdminRecordLabel(value) || 'Открыть файл';

  return getAdminRecordLabel(value) || 'Подробнее';
};

/**
 * Проверяет пустое значение admin-поля.
 * @param value Значение
 */
const isEmptyAdminValue = (value: unknown) => {
  return value === null || value === undefined || value === '';
};

/**
 * Форматирует любое admin-значение без контекста записи.
 * @param value Значение
 */
export const formatAdminValue = (value: unknown): string => {
  if (isEmptyAdminValue(value)) return '—';
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'number') return value.toLocaleString('ru-RU');
  if (typeof value === 'string') return formatAdminString(value);
  if (Array.isArray(value)) return formatAdminArray(value);
  if (isAdminRecord(value)) return formatAdminRecordValue(value);

  return String(value);
};

/**
 * Возвращает значение поля с учётом `*_label`/`*_labels`.
 * @param record Запись API
 * @param field API key
 */
export const getAdminFieldDisplayValue = (
  record: TAdminRecord,
  field: string
) => {
  return record[`${field}_labels`] ?? record[`${field}_label`] ?? record[field];
};

/**
 * Форматирует admin-поле в контексте записи.
 * @param record Запись API
 * @param field API key
 */
export const formatAdminFieldValue = (record: TAdminRecord, field: string) => {
  const value = getAdminFieldDisplayValue(record, field);
  if (field === 'reasons_summary') {
    return formatActorComplaintReasonsSummary(
      value,
      getAnyComplaintReasonLabel
    );
  }
  if (field === 'reasons') return formatActorComplaintReasons(value);

  const staticLabel = getStaticValueLabel(field, value);

  if (staticLabel) return staticLabel;
  if (field === 'year' || field.endsWith('_year')) {
    return isEmptyAdminValue(value) ? '—' : String(value);
  }
  if (field.endsWith('_at')) return formatAdminDateTime(value);
  if (field.endsWith('_date')) return formatAdminDate(value);

  return formatAdminValue(value);
};
